/**
 * Topic namespace (per spec §1.5.2).
 * NOTE: docs 006 and 007 disagree on the prefix — this is Q1 in the spec.
 * Implementation uses VITE_TOPIC_PREFIX so swapping is a config change, not a code change.
 */

import { config } from '@/lib/config';

const P = config.mqtt.topicPrefix;

export const topics = {
  // per-drone
  telemetry: (id: string) => `${P}/${id}/telemetry`,
  proximity: (id: string) => `${P}/${id}/proximity`,
  events: (id: string) => `${P}/${id}/events`,
  c2links: (id: string) => `${P}/${id}/c2links`,
  oi: (id: string) => `${P}/${id}/oi`,

  // global
  targets: `${P}/targets`,
  surveillance: `${P}/surveillance`,
  infrastructure: `${P}/infrastructure`,
  services: `${P}/services`,

  // wildcards
  all: `${P}/#`,
};

/**
 * Canonical v1 Events & Alerts topics (FLYsafe MQTT Topic doc). Tenant-scoped
 * under `v1/{tenant}/…`; the serial (`{serial}`) is the per-drone scope.
 */
const T = config.tenant.id;
export const v1Topics = {
  droneEvents: (serial: string) => `v1/${T}/aircraft/${serial}/drones/events`,
  droneEventsWild: `v1/${T}/aircraft/+/drones/events`,
  aircraftAlerts: (serial: string) => `v1/${T}/aircraft/${serial}/alerts`,
  aircraftAlertsWild: `v1/${T}/aircraft/+/alerts`,
  aircraftAlertsSevWild: `v1/${T}/aircraft/+/alerts/+`,
  broadcastAlerts: `v1/${T}/shared/alerts/broadcast`,
};

/** True for any canonical v1 events/alerts topic (routing guards). */
export function isV1EventTopic(topic: string): boolean {
  return topic.startsWith('v1/') && topic.endsWith('/drones/events');
}
export function isV1AlertTopic(topic: string): boolean {
  return topic.startsWith('v1/') && topic.includes('/alerts');
}

/* ------------------------------------------------------------------ */
/*  `v1/output` — category-first namespace (ticket 86e2jbe3d)          */
/* ------------------------------------------------------------------ */

/**
 * The new namespace is `v1/{category}/{scope}/{id}/{messageType}` where
 * category is `shared | input | output | internal`. Tenant is NOT in the
 * path — every tenant has its own EMQX broker, so the connection fixes
 * it. That is why none of these builders take a tenant id.
 *
 * Verified live on sandbox 2026-07-30: `networkid` and `telemetry` are
 * publishing at ~1.7 Hz per aircraft; the rest are defined here so the
 * routing table is complete when the backend enables them.
 */
export const OUTPUT_ROOT = 'v1/output';
const AIRCRAFT = `${OUTPUT_ROOT}/aircraft`;

export const outputTopics = {
  // per-aircraft
  //
  // NOTE: no `events` builder here. `v1/output/aircraft/{sn}/events` was
  // published briefly by the NID/NID-DD jobs (ticket 86e2jbaqr Parts 1–2)
  // before being corrected to follow the FLYsafe MQTT doc, which specifies
  // events as tenant-wide only (`tenantEvents` below). Confirmed dead on
  // the wire: 0 messages across every check this session, vs hundreds on
  // the tenant-wide topic in the same windows. Do not resurrect this
  // builder without reconfirming with the backend first.
  networkid: (sn: string) => `${AIRCRAFT}/${sn}/networkid`,
  telemetry: (sn: string) => `${AIRCRAFT}/${sn}/telemetry`,
  oiDeclaration: (sn: string) => `${AIRCRAFT}/${sn}/oi/declaration`,
  // ETA-to-next-waypoint predictions. Moved off `v1/output/alerts/waypoint_pred`
  // to this per-aircraft key only (ENG-173, confirmed live on sandbox).
  waypointPred: (sn: string) => `${AIRCRAFT}/${sn}/waypoint_pred`,
  // `waypoint_approached` is published on this per-aircraft key AND, unchanged,
  // on `v1/output/alerts/waypoint_approached` — the identical message (same
  // `alertId`). The waypoint overlay reads this per-aircraft copy, like
  // `waypoint_pred`; the alerts-key copy still feeds the Live Alerts pane and is
  // NOT applied to the waypoint store, so nothing is counted twice. (Measured
  // 2026-10-07: this copy arrives 0–11 s after the alerts-key one.)
  waypointApproached: (sn: string) => `${AIRCRAFT}/${sn}/waypoint_approached`,

  // wildcards. `+` matches exactly one level, which is what keeps the
  // per-field branch out — see PER_FIELD_SEGMENT below.
  networkidWild: `${AIRCRAFT}/+/networkid`,
  telemetryWild: `${AIRCRAFT}/+/telemetry`,
  oiDeclarationWild: `${AIRCRAFT}/+/oi/declaration`,
  waypointPredWild: `${AIRCRAFT}/+/waypoint_pred`,
  waypointApproachedWild: `${AIRCRAFT}/+/waypoint_approached`,

  // tenant-wide — the doc-correct location for events (see note above) and
  // alerts.
  tenantEvents: `${OUTPUT_ROOT}/events`,
  tenantAlertsWild: `${OUTPUT_ROOT}/alerts/+`,

  /**
   * Every per-aircraft message type in one subscription, and ONLY those.
   *
   * `v1/output/aircraft/+/+` is five levels, so it matches
   * `.../{sn}/networkid` and `.../{sn}/telemetry` but cannot match the
   * six-level `.../{sn}/drones/{fieldName}`. That distinction matters: the
   * per-field branch republishes all 56 NID fields as individual messages,
   * which measured 97 msg/s per aircraft against 3.5 msg/s for the
   * consolidated pair — a 28× amplification carrying zero unique data
   * (every per-field topic is an alias of a field already present).
   *
   * Use this for the Data Stream "subscribe all" affordance. Never use
   * `v1/output/#` or `v1/output/aircraft/+/#`.
   */
  allPerAircraft: `${AIRCRAFT}/+/+`,
} as const;

/** Topic segment that marks the per-field alias branch. */
export const PER_FIELD_SEGMENT = '/drones/';

/** True for `v1/output/aircraft/{sn}/drones/{fieldName}` — the alias
 *  branch we deliberately don't consume. Used as a defence-in-depth gate
 *  in the router so a stray wildcard elsewhere can't flood the store. */
export function isPerFieldAliasTopic(topic: string): boolean {
  return topic.startsWith(`${AIRCRAFT}/`) && topic.includes(PER_FIELD_SEGMENT);
}

/** Parse an output-namespace topic into its aircraft id + message type.
 *  Returns null for anything outside `v1/output/aircraft/{sn}/…` and for
 *  the per-field alias branch. */
export function parseOutputTopic(topic: string): { sn: string; kind: string } | null {
  if (!topic.startsWith(`${AIRCRAFT}/`)) return null;
  if (isPerFieldAliasTopic(topic)) return null;
  const rest = topic.slice(AIRCRAFT.length + 1);
  const slash = rest.indexOf('/');
  if (slash <= 0) return null;
  const sn = rest.slice(0, slash);
  const kind = rest.slice(slash + 1);
  return kind ? { sn, kind } : null;
}

export type TopicKind =
  | 'telemetry'
  | 'proximity'
  | 'events'
  | 'c2links'
  | 'oi'
  | 'targets'
  | 'surveillance'
  | 'infrastructure'
  | 'services';

/**
 * Parse a topic string and extract the kind + droneId (if any).
 * Returns null if topic doesn't match our namespace.
 */
const PER_DRONE_KINDS: Record<string, TopicKind> = {
  telemetry: 'telemetry',
  proximity: 'proximity',
  events: 'events',
  c2links: 'c2links',
  oi: 'oi',
};
const GLOBAL_KINDS: Record<string, TopicKind> = {
  targets: 'targets',
  surveillance: 'surveillance',
  infrastructure: 'infrastructure',
  services: 'services',
};

export function parseTopic(topic: string): { kind: TopicKind; droneId?: string } | null {
  if (!topic.startsWith(`${P}/`)) return null;
  const rest = topic.slice(P.length + 1);
  const parts = rest.split('/');
  if (parts.length === 1) {
    const k = GLOBAL_KINDS[parts[0]!];
    return k ? { kind: k } : null;
  }
  if (parts.length === 2) {
    const k = PER_DRONE_KINDS[parts[1]!];
    if (!k) return null;
    return { kind: k, droneId: parts[0]! };
  }
  return null;
}
